import { useEffect, useRef, useState } from 'react';

const WIDTH = 72;
const HEIGHT = 96;

/** First page of a local PDF drawn to a small canvas. Loads pdf.js on demand; shows nothing if the file can't be rendered. */
export function PdfThumb({ file, label }: { file: File; label: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let destroy: (() => void) | null = null;
    setFailed(false); setReady(false);
    (async () => {
      const { openPdf } = await import('../api/pdfjs');
      const pdf = await openPdf(new Uint8Array(await file.arrayBuffer()));
      destroy = () => { void pdf.close(); };
      if (cancelled) { destroy(); return; }
      const doc = await pdf.task.promise;
      const page = await doc.getPage(1);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const scale = Math.min(WIDTH / base.width, HEIGHT / base.height) * dpr;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      if (!cancelled) setReady(true);
    })().catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; destroy?.(); };
  }, [file]);

  if (failed) return null;
  return (
    <span className="sx-file-thumb" style={{ width: WIDTH, height: HEIGHT }}>
      <canvas ref={canvasRef} role="img" aria-label={label} style={{ visibility: ready ? 'visible' : 'hidden' }} />
    </span>
  );
}
